import { useReducer, useEffect, useMemo } from 'react';
import useApi, {
  GET_DASHBOARD_WIDGETS,
  USER_DELETE_DASHBOARD,
  USER_DELETE_WIDGET
} from 'hooks/useApi';
import useNavigate from 'hooks/useNavigate';
import { initialState, reducer } from './config';
import * as ACTIONS from './actions';

export const useDashboard = id => {
  const [state, makeRequest] = useApi();
  const [dashboardState, dispatch] = useReducer(reducer, initialState);
  const { isLoading, isError, data } = state;

  // Memoize the request in order to prevent unneeded API calls before the user gets redirected to the default dashboard id in case the ID is missing initially
  // makeRequest in it's core is just setState which is guaranteed to be static by React and it won't affect change
  useMemo(() => {
    const reqParams = id && GET_DASHBOARD_WIDGETS({ id });
    makeRequest(reqParams);
  }, [id, makeRequest]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (isError) {
      return;
    }

    if (data) {
      dispatch({ type: ACTIONS.DASHBOARD_READY, payload: data });
    }
  }, [isLoading, isError, data]);

  return [state, dashboardState, dispatch];
};

export const useDeleteDashboard = () => {
  const [state, makeRequest] = useApi();
  const { isLoading, isError, data } = state;

  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (isError) {
      return;
    }

    if (data !== undefined) {
      navigate('/');
    }
  }, [isLoading, isError, data, navigate]);

  const deleteDashboard = ({ id }) => {
    const params = USER_DELETE_DASHBOARD({ id });
    makeRequest(params);
  };

  return [state, deleteDashboard];
};

export const useDeleteWidget = (dispatch, id, onClose) => {
  const [state, makeRequest] = useApi();
  const { isLoading, isError, data } = state;

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (isError) {
      return;
    }

    if (data !== undefined) {
      dispatch({ type: ACTIONS.DELETE_WIDGET, payload: id });
      onClose();
    }
  }, [isLoading, isError, data, onClose, dispatch, id]);

  const deleteWidget = () => {
    const params = USER_DELETE_WIDGET({ id });
    makeRequest(params);
  };

  return [state, deleteWidget];
};
